import React, { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { Box, Typography } from '@mui/material';
import { useAuth, USER_ROLES, PERMISSIONS } from '../../contexts/AuthContext';
import { SEARCE_COLORS } from '../../theme';
import { CheckingGateSpinner } from './CheckingGateSpinner';

interface ProtectedRouteProps {
  children: ReactNode;
  requiredPermissions?: string[];
  requiredRoles?: string[];
  fallbackRoute?: string;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermissions = [],
  requiredRoles = [],
  fallbackRoute = '/login',
}) => {
  const { isAuthenticated, isLoading, user, hasPermission, hasAnyRole } = useAuth();
  const location = useLocation();

  if (isLoading) {
    return <CheckingGateSpinner message="Checking authentication…" layout="fullscreen" />;
  }

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to={fallbackRoute} state={{ from: location }} replace />;
  }

  // Check role requirements
  if (requiredRoles.length > 0 && !hasAnyRole(requiredRoles)) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          backgroundColor: SEARCE_COLORS.background,
          px: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            color: SEARCE_COLORS.textPrimary,
            fontWeight: 600,
            mb: 2,
          }}
        >
          Access Denied
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: SEARCE_COLORS.textPrimary,
            textAlign: 'center',
            mb: 1,
          }}
        >
          You don't have the required role to access this page.
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ textAlign: 'center' }}
        >
          Required roles: {requiredRoles.join(', ')}
        </Typography>
        <Typography
          variant="body2"
          color="textSecondary"
          sx={{ mt: 1, textAlign: 'center' }}
        >
          Your role: {user?.role?.display_name || user?.role?.name || 'Unknown'}
        </Typography>
      </Box>
    );
  }

  // Check permission requirements
  if (requiredPermissions.length > 0) {
    const hasAllPermissions = requiredPermissions.every((permission) =>
      hasPermission(permission)
    );

    if (!hasAllPermissions) {
      return (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            backgroundColor: SEARCE_COLORS.background,
            px: 3,
          }}
        >
          <Typography
            variant="h5"
            sx={{
              color: SEARCE_COLORS.textPrimary,
              fontWeight: 600,
              mb: 2,
            }}
          >
            Access Denied
          </Typography>
          <Typography
            variant="body1"
            sx={{
              color: SEARCE_COLORS.textPrimary,
              textAlign: 'center',
              mb: 1,
            }}
          >
            You don't have the required permissions to access this page.
          </Typography>
          <Typography
            variant="body2"
            color="textSecondary"
            sx={{ textAlign: 'center' }}
          >
            Required permissions: {requiredPermissions.join(', ')}
          </Typography>
        </Box>
      );
    }
  }

  // All checks passed, render the protected content
  return <>{children}</>;
};

export default ProtectedRoute;

// Pre-configured protected route components for common use cases
export const AdminRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredRoles={[USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN]}>
    {children}
  </ProtectedRoute>
);

export const ManagerRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute
    requiredRoles={[
      USER_ROLES.MANAGER,
      USER_ROLES.SBU_LEAD,
      USER_ROLES.EXECUTIVE,
      USER_ROLES.ADMIN,
      USER_ROLES.SUPER_ADMIN,
    ]}
  >
    {children}
  </ProtectedRoute>
);

export const AnalystRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute
    requiredRoles={[
      USER_ROLES.ANALYST,
      USER_ROLES.MANAGER,
      USER_ROLES.SBU_LEAD,
      USER_ROLES.EXECUTIVE,
      USER_ROLES.ADMIN,
      USER_ROLES.SUPER_ADMIN,
    ]}
  >
    {children}
  </ProtectedRoute>
);

// Permission-based route components
export const PeopleAnalyticsRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_PEOPLE_ANALYTICS]}>
    {children}
  </ProtectedRoute>
);

export const ProjectsRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermissions={[PERMISSIONS.VIEW_PROJECTS]}>
    {children}
  </ProtectedRoute>
);

export const UserManagementRoute: React.FC<{ children: ReactNode }> = ({ children }) => (
  <ProtectedRoute requiredPermissions={[PERMISSIONS.MANAGE_USERS]}>
    {children}
  </ProtectedRoute>
);
